import { FC, PropsWithChildren, ReactNode, useState } from 'react'

import { Content, Root, Trigger } from '@radix-ui/react-collapsible'
import { styled } from 'styled-components'

import Icon from 'core/components/Icon'
import Clickable from 'core/components/lib/Clickable'
import variables from 'core/styles/variables'

type HelpTextProps = PropsWithChildren<{
  className?: string
  text?: ReactNode
  title?: string
  subTitle?: string
  helpTextMargin?: string
  iconMargin?: string
}>

const HelpText: FC<HelpTextProps> = ({
  className,
  children,
  text,
  title,
  helpTextMargin = '0 8px',
  iconMargin,
}) => {
  const [isOpen, onOpenChange] = useState(false)

  return text ? (
    <Root onOpenChange={onOpenChange} open={isOpen}>
      <Row className={className}>
        {children}

        <Trigger asChild>
          <Clickable ariaLabel='Toggle help' variant='ghost'>
            <Icon
              fontSize='24px'
              margin={iconMargin}
              name={isOpen ? 'highlight_off' : 'help'}
              outlined
            />
          </Clickable>
        </Trigger>
      </Row>

      <Box margin={helpTextMargin}>
        {title && <Title>{title}</Title>}
        <Paragraphs>{text}</Paragraphs>
      </Box>
    </Root>
  ) : (
    <Row className={className}>{children}</Row>
  )
}

export default HelpText

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-right: 24px;
`

const Box = styled(Content)<{ margin?: string }>`
  position: relative;
  margin: ${(p) => p.margin};
  border-radius: 8px;
  background-color: ${variables.colorBlueLighten};
  padding: 16px;
  color: ${variables.colorBlack70};
  font-size: 14px;
`

const Title = styled.p`
  margin-top: 0;
  font-weight: 500;
`

const Paragraphs = styled.div`
  margin-top: 0;
  margin-bottom: 0;
  white-space: pre-wrap;

  ul {
    margin: 8px 0;
    padding-left: 16px;
  }

  p {
    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }
`
